import { useState, useEffect } from "react";
import {
  Drawer,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  ListItemButton,
  Box,
  Badge,
} from "@mui/material";

import { useSelector, useDispatch } from "react-redux";
import { drawerHandler } from "src/store/reducers/drawerSlice";

import { useLocation, useNavigate } from "react-router-dom";
import { useMutation } from "@apollo/client";
import useActiveUserDetail from "src/utils/shared/useActiveUserDetail";
import LogoutQuery from "src/graphql/query/auth/logout";
import { ChatContext } from "src/context/chats";
import BrandedLoader from "src/components/shared/atoms/BrandedLoader";

import DashboardIconSvg from "./Icons/dashboard";
import ChatIcon from "./Icons/chat";
import UserIcon from "./Icons/users";
import ReportIcon from "./Icons/reports";
import CallLogsIcon from "./Icons/callLogs";
import SettingsIcon from "./Icons/settings";
import LogoutIcon from "./Icons/logout";

import useStyles from "./Styles";

const drawerItemsTop = [
  {
    text: "Dashboard",
    icon: (fill: string) => <DashboardIconSvg fill={fill} />,
    path: "/",
    id: "tour-dashboard",
  },
  {
    text: "Chat",
    icon: (fill: string) => <ChatIcon fill={fill} />,
    path: "/chats",
    id: "tour-chats",
  },
  {
    text: "Users",
    icon: (fill: string) => <UserIcon fill={fill} />,
    path: "/users",
    id: "tour-users",
  },
  {
    text: "Reports",
    icon: (fill: string) => <ReportIcon fill={fill} />,
    path: "/reports",
    id: "tour-reports",
  },
  {
    text: "Call Logs",
    icon: (fill: string) => <CallLogsIcon fill={fill} />,
    path: "/call-logs",
    id: "tour-call-logs",
  },
];

const drawerItemsBottom = [
  {
    text: "Settings",
    icon: (fill: string) => <SettingsIcon fill={fill} />,
    path: "/settings",
    id: "tour-settings",
  },
  {
    text: "Logout",
    icon: (fill: string) => <LogoutIcon fill={fill} />,
    path: "/logout",
    isLogout: true,
  },
];

const Sidebar = () => {
  const { classes } = useStyles();
  const location = useLocation();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);

  const { panelType } = useActiveUserDetail();

  const [logout, { data: logoutQueryResult, loading: logoutQueryLoading }] =
    useMutation(LogoutQuery);
  const handleLogout = () => logout();

  useEffect(() => {
    if (logoutQueryResult?.logout?.success) {
      localStorage.removeItem("accessToken");
      localStorage.removeItem("ActiveUserdetail");
      localStorage.removeItem("userName");
      navigate("/login");
      setLoading(false);
    }
  }, [logoutQueryResult]);

  useEffect(() => {
    if (logoutQueryLoading) setLoading(true);
  }, [logoutQueryLoading]);

  const { drawer } = useSelector((state: any) => state.drawer);
  const {
    fbUnreadCount,
    IgUnreadCount,
    WaUnreadCount,
    fbCommentsUnreadCount,
    chatbotUnreadCount,
  } = ChatContext();

  const totalUnreadCount =
    fbUnreadCount +
    IgUnreadCount +
    WaUnreadCount +
    fbCommentsUnreadCount +
    chatbotUnreadCount;

  const dispatch = useDispatch();

  const handleMouseEnter = () => dispatch(drawerHandler(true));
  const handleMouseLeave = () => dispatch(drawerHandler(false));

  return (
    <>
      {loading && <BrandedLoader message="Logging out..." />}

      <Drawer
        variant="permanent"
        sx={{
          zIndex: 0,
          display: { xs: "none", sm: "none", md: "block" },
          [`& .MuiDrawer-paper`]: {
            width: drawer ? 240 : 85,
            transition: "width 0.3s ease-in-out",
            overflowX: "hidden",
            border: "none",
          },
        }}
        open={drawer}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
      >
        <Box
          className={classes.itemsBox}
          sx={{
            height: "100%",
            display: "flex",
            flexDirection: "column",
            justifyContent: "space-between",
          }}
        >
          {/* Top Items */}
          <Box>
            <List>
              {drawerItemsTop.map(({ text, icon, path, id }) => {
                if (panelType === "AGENT" && text === "Users") return null;
                const isDashboard = path === "/" && location.pathname === "/";
                const isActive =
                  isDashboard ||
                  (path !== "/" && location.pathname.includes(path));

                return (
                  <ListItem disablePadding key={text} id={id}>
                    <ListItemButton
                      disableRipple
                      onClick={() => {
                        if (location.pathname !== path) {
                          navigate(path);
                        }
                      }}
                      sx={{ px: 2, py: 1 }}
                      className={`${classes.listItemButton} ${
                        isActive ? classes.activeButtonStyle : ""
                      }`}
                    >
                      <ListItemIcon
                        sx={{
                          color: isActive ? "#fff" : undefined,
                          justifyContent: "center",
                          minWidth: 0,
                          marginRight: "25px",
                          // minWidth: drawer ? 40 : "auto",
                        }}
                      >
                        <Badge
                          badgeContent={text === "Chat" ? totalUnreadCount : 0}
                          color="error"
                          overlap="circular"
                          sx={{
                            "& .MuiBadge-badge": {
                              fontSize: "10px",
                              height: "18px",
                              minWidth: "18px",
                              backgroundColor: isActive ? "#fff" : "#ff4d4d",
                              color: isActive ? "#4D1277" : "#fff",
                              border: `2px solid ${
                                isActive ? "#4D1277" : "#fff"
                              }`,
                              boxShadow: isActive
                                ? "0 4px 10px rgba(0,0,0,0.2)"
                                : "0 2px 4px rgba(0,0,0,0.1)",
                              top: -4,
                              right: -4,
                              fontWeight: 800,
                              display:
                                totalUnreadCount > 0 &&
                                text === "Chat" &&
                                location.pathname !== "/chats"
                                  ? "flex"
                                  : "none",
                            },
                          }}
                        >
                          {typeof icon === "function"
                            ? icon(isActive ? "#fff" : "#000")
                            : icon}
                        </Badge>
                      </ListItemIcon>
                      <ListItemText
                        primary={text}
                        primaryTypographyProps={{
                          className: isActive
                            ? classes.activeTextStyle
                            : classes.baseTextStyle,
                          sx: {
                            color: isActive ? "#fff" : "inherit",
                            opacity: drawer ? 1 : 0,
                            visibility: drawer ? "visible" : "hidden",
                            transition: drawer
                              ? "opacity 0.2s ease-in-out 0.15s, visibility 0.2s ease-in-out 0.15s"
                              : "opacity 0.1s ease-in-out, visibility 0.1s ease-in-out",
                            whiteSpace: "nowrap",
                            display: "block",
                          },
                        }}
                      />
                    </ListItemButton>
                  </ListItem>
                );
              })}
            </List>
          </Box>

          {/* Bottom Items */}
          <Box>
            <List>
              {drawerItemsBottom.map(({ text, icon, path, isLogout, id }) => {
                const isDashboard = path === "/" && location.pathname === "/";
                const isActive =
                  isDashboard ||
                  (path !== "/" && location.pathname.includes(path));

                return (
                  <ListItem disablePadding key={text} id={id}>
                    <ListItemButton
                      disableRipple
                      onClick={() => {
                        if (isLogout) {
                          handleLogout();
                        } else if (location.pathname !== path) {
                          navigate(path);
                        }
                      }}
                      sx={{ px: 2, py: 1 }}
                      className={`${classes.listItemButton} ${
                        isActive ? classes.activeButtonStyle : ""
                      }`}
                    >
                      <ListItemIcon
                        sx={{
                          color: isActive ? "#fff" : undefined,
                          justifyContent: "center",
                          minWidth: 0,
                          marginRight: "25px",
                          // minWidth: drawer ? 40 : "auto",
                        }}
                      >
                        {typeof icon === "function"
                          ? icon(isActive ? "#fff" : "#000")
                          : icon}
                      </ListItemIcon>
                      <ListItemText
                        primary={text}
                        primaryTypographyProps={{
                          className: isActive
                            ? classes.activeTextStyle
                            : classes.baseTextStyle,
                          sx: {
                            color: isActive ? "#fff" : "inherit",
                            opacity: drawer ? 1 : 0,
                            visibility: drawer ? "visible" : "hidden",
                            transition: drawer
                              ? "opacity 0.2s ease-in-out 0.15s, visibility 0.2s ease-in-out 0.15s"
                              : "opacity 0.1s ease-in-out, visibility 0.1s ease-in-out",
                            whiteSpace: "nowrap",
                            display: "block",
                          },
                        }}
                      />
                    </ListItemButton>
                  </ListItem>
                );
              })}
            </List>
          </Box>
        </Box>
      </Drawer>
    </>
  );
};

export default Sidebar;
